
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/base/Button';

export default function OxyAISection() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    city: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.city) return;

    setIsSubmitting(true);
    try {
      const formDataToSubmit = new URLSearchParams();
      formDataToSubmit.append('name', formData.name);
      formDataToSubmit.append('email', formData.email);
      formDataToSubmit.append('city', formData.city);

      await fetch('https://readdy.ai/api/form/d30mc3iuu7nqqcqu7tcg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSubmit.toString(),
      });

      setIsSubmitted(true);
      setFormData({ name: '', email: '', city: '' });
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleJoinWaitlist = () => {
    navigate('/app');
  };

  const handleSponsorFamily = () => {
    navigate('/donate');
  };

  const steps = [
    {
      step: '01',
      title: 'Quick Self-Check',
      description: 'Answer simple questions about your health and lifestyle in under 2 minutes.',
      icon: 'ri-question-line'
    },
    {
      step: '02',
      title: 'OxyScore™',
      description: 'Get your personalized oxygen deficiency risk score instantly.',
      icon: 'ri-heart-pulse-line'
    },
    {
      step: '03',
      title: 'Personalized Guidance',
      description: 'Receive tailored recommendations for improving your oxygen health.',
      icon: 'ri-guide-line'
    }
  ];

  return (
    <section className="py-20 bg-[#FAFAFA]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-[#111111] mb-6">
                Your Oxygen Score,
                <br />
                <span className="text-[#12B6B6]">Anytime, Anywhere.</span>
              </h2>
              <p className="text-xl text-gray-700 leading-relaxed">
                OxyAI™ makes oxygen deficiency screening accessible to everyone. 
                Our AI-powered app provides instant health insights without any equipment.
              </p>
            </div>

            {/* Steps */}
            <div className="space-y-6">
              {steps.map((step, index) => (
                <div key={index} className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#12B6B6] text-white font-bold">
                      <i className={`${step.icon} text-lg`}></i>
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <span className="text-sm font-mono text-[#12B6B6]">{step.step}</span>
                      <h3 className="text-lg font-bold text-[#111111]">{step.title}</h3>
                    </div>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={handleJoinWaitlist}
                variant="primary" 
                size="lg"
                className="px-10 bg-white text-[#12B6B6] hover:bg-gray-100"
              >
                Join the App Waitlist
              </Button>
              <Button 
                onClick={handleSponsorFamily}
                variant="outline" 
                size="lg"
                className="px-10 border-white text-white hover:bg-white hover:text-[#12B6B6]"
              >
                Sponsor a Family
              </Button>
            </div>
          </div>

          {/* Right - Waitlist Form */}
          <div className="bg-white rounded-3xl p-8 shadow-lg">
            <div className="text-center mb-8">
              <div className="w-16 h-16 mx-auto mb-4 bg-[#12B6B6]/10 rounded-2xl flex items-center justify-center">
                <i className="ri-smartphone-line text-3xl text-[#12B6B6]"></i>
              </div>
              <h3 className="text-2xl font-bold text-[#111111] mb-2">Join the Waitlist</h3>
              <p className="text-gray-600">Be the first to access OxyAI™ when it launches.</p>
            </div>

            {isSubmitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h4 className="text-xl font-bold text-[#111111] mb-2">You're on the list!</h4>
                <p className="text-gray-600">We'll notify you when OxyAI™ is ready.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} id="waitlist-form" data-readdy-form className="space-y-4">
                <div>
                  <input
                    type="text"
                    name="name"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="city"
                    placeholder="City"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#12B6B6] focus:border-transparent text-sm"
                    required
                  />
                </div>
                <Button 
                  type="submit" 
                  variant="primary" 
                  className="w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Joining...' : 'Join Waitlist'}
                </Button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
